/**
 * i18n bootstrap.
 *
 * Detection chain (in order; first hit wins):
 *   1. localStorage 'sparts.lang' — instant on subsequent visits, same device
 *   2. (post-login) user.preferred_locale — wired up in AuthContext, gives
 *      cross-device persistence once the user has signed in
 *   3. navigator.language — Accept-Language fallback for first visit (gives
 *      Lebanese users `ar` automatically)
 *   4. fallback 'en'
 *
 * Persistence on change: i18next-browser-languagedetector writes localStorage;
 * the LanguageSwitcher additionally fires PATCH /users/me/preferences (when
 * logged in) so the choice follows the user across devices/browsers.
 *
 * Namespace strategy: per-feature JSON files under en/<ns>.json and ar/<ns>.json.
 * This phase wires `common`; later phases add insurance/mechanic/supplier/
 * notifications/auth/validation. Each namespace lazy-loads on first use.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enCommon from './en/common.json';
import arCommon from './ar/common.json';
import enAuth from './en/auth.json';
import arAuth from './ar/auth.json';
import enNav from './en/navigation.json';
import arNav from './ar/navigation.json';
import enInsurance from './en/insurance.json';
import arInsurance from './ar/insurance.json';
import enMechanic from './en/mechanic.json';
import arMechanic from './ar/mechanic.json';
import enNotifications from './en/notifications.json';
import arNotifications from './ar/notifications.json';
import enTeam from './en/team.json';
import arTeam from './ar/team.json';
import enSettings from './en/settings.json';
import arSettings from './ar/settings.json';
import enSupplier from './en/supplier.json';
import arSupplier from './ar/supplier.json';
import enLocation from './en/location.json';
import arLocation from './ar/location.json';
import enValidation from './en/validation.json';
import arValidation from './ar/validation.json';

export const SUPPORTED_LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = 'sparts.lang';

/**
 * RTL languages — keep this list in one place so layout, fonts, and the dir
 * attribute stay in sync.
 */
const RTL_LOCALES: Locale[] = ['ar'];
export function isRtl(locale: string | undefined): boolean {
	return RTL_LOCALES.includes((locale || '').slice(0, 2) as Locale);
}

/**
 * Reflects the active locale onto the document. Called on init AND every time
 * `i18n.changeLanguage()` resolves. Without this:
 *   - Tailwind's rtl: variants don't trigger (they read off `[dir=rtl]`)
 *   - Cairo font rule (set in tailwind config under .font-arabic) doesn't apply
 *   - Screen readers and copy/paste get inconsistent direction handling
 */
function applyDocumentDir(lng: string) {
	if (typeof document === 'undefined') return;
	const dir = isRtl(lng) ? 'rtl' : 'ltr';
	document.documentElement.setAttribute('dir', dir);
	document.documentElement.setAttribute('lang', lng);
	if (isRtl(lng)) {
		document.documentElement.classList.add('font-arabic');
	} else {
		document.documentElement.classList.remove('font-arabic');
	}
}

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		resources: {
			en: {
				common: enCommon,
				auth: enAuth,
				navigation: enNav,
				insurance: enInsurance,
				mechanic: enMechanic,
				notifications: enNotifications,
				team: enTeam,
				settings: enSettings,
				supplier: enSupplier,
				location: enLocation,
				validation: enValidation,
			},
			ar: {
				common: arCommon,
				auth: arAuth,
				navigation: arNav,
				insurance: arInsurance,
				mechanic: arMechanic,
				notifications: arNotifications,
				team: arTeam,
				settings: arSettings,
				supplier: arSupplier,
				location: arLocation,
				validation: arValidation,
			},
		},
		ns: ['common', 'auth', 'navigation', 'insurance', 'mechanic', 'notifications', 'team', 'settings', 'supplier', 'validation'],
		defaultNS: 'common',
		fallbackLng: 'en',
		supportedLngs: SUPPORTED_LOCALES as unknown as string[],
		// Always render the closest locale we have — `ar-LB` falls back to `ar`.
		nonExplicitSupportedLngs: true,
		load: 'languageOnly',
		interpolation: {
			escapeValue: false, // React escapes by default
		},
		detection: {
			order: ['localStorage', 'navigator', 'htmlTag'],
			lookupLocalStorage: LOCALE_STORAGE_KEY,
			caches: ['localStorage'],
		},
		react: {
			useSuspense: false, // we ship strings inline so no Suspense boundary needed
		},
	});

// Reflect the resolved locale on first paint and on every change.
applyDocumentDir(i18n.resolvedLanguage || i18n.language || 'en');
i18n.on('languageChanged', applyDocumentDir);

// Install the localised Zod errorMap once i18n has its resources in place.
// Doing this here (rather than from src/index.tsx) keeps it a side-effect of
// importing this module — every entry point that bootstraps i18n also gets
// the localised validation messages "for free".
//
// Lazy require avoids a circular-import surprise: zodErrorMap.ts itself
// imports this module to call i18n.t.
//
/* eslint-disable @typescript-eslint/no-var-requires */
try {
	const { installZodLocalisedErrors } = require('./zodErrorMap');
	installZodLocalisedErrors();
} catch (e) {
	// Zod or the error-map module isn't available; falling back to Zod's
	// english defaults is fine — schemas keep working, just untranslated.
	// eslint-disable-next-line no-console
	console.warn('[i18n] Zod localised errors not installed:', e);
}
/* eslint-enable @typescript-eslint/no-var-requires */

/**
 * Add a namespace's translations at runtime. Used by `loadNamespace` so a
 * page can pull in just the strings it needs without bundling every namespace
 * up-front. The Promise resolves once both locales are added.
 */
export async function addNamespaceResources(
	ns: string,
	resources: Record<Locale, Record<string, any>>
): Promise<void> {
	for (const lng of SUPPORTED_LOCALES) {
		i18n.addResourceBundle(lng, ns, resources[lng], true, true);
	}
}

export default i18n;
